import { WebSocketMessage } from '@/types/queue.types';
import { debug } from '@/config/constants';

const LONG_POLL_DURATION = 10000; // 10 seconds per connection cycle

class WebSocketService {
  private ws: WebSocket | null = null;
  private url: string = '';
  private queueId: string = '';
  private messageCallback: ((message: WebSocketMessage) => void) | null = null;
  private errorCallback: ((error: Error) => void) | null = null;
  private closeCallback: (() => void) | null = null;
  private shouldReconnect: boolean = false;
  private longPollTimer: ReturnType<typeof setTimeout> | null = null;

  connect(url: string, queueId: string): Promise<void> {
    // If already running long-poll for this queueId, don't restart
    if (this.shouldReconnect && this.queueId === queueId) {
      return Promise.resolve();
    }

    // Disconnect any existing connection
    if (this.ws) {
      this.disconnect();
    }

    this.url = url;
    this.queueId = queueId;
    this.shouldReconnect = true;

    return this.openConnection();
  }

  private openConnection(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.shouldReconnect) return;

      try {
        const wsUrl = `${this.url}?queueId=${this.queueId}`;
        debug.log('Connecting to WebSocket for queue:', this.queueId);

        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          debug.log('WebSocket connected (long-poll cycle)');

          // Schedule close after LONG_POLL_DURATION to get a fresh connectionId
          this.longPollTimer = setTimeout(() => {
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
              debug.log('Long-poll cycle complete, reconnecting for fresh connectionId');
              this.ws.close(1000, 'long-poll-cycle');
            }
          }, LONG_POLL_DURATION);

          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            if (this.messageCallback) {
              this.messageCallback(message);
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        this.ws.onerror = (event) => {
          console.error('WebSocket error:', event);
          reject(new Error('WebSocket connection error'));
        };

        this.ws.onclose = () => {
          debug.log('WebSocket closed');
          this.clearLongPollTimer();

          if (this.closeCallback) {
            this.closeCallback();
          }

          // Reconnect immediately if still active — fresh connectionId each cycle
          if (this.shouldReconnect) {
            setTimeout(() => {
              this.openConnection().catch(console.error);
            }, 100); // Brief pause before reconnecting
          }
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.clearLongPollTimer();

    if (this.ws) {
      this.ws.onclose = null; // Prevent reconnection loop
      this.ws.close();
      this.ws = null;
    }
  }

  private clearLongPollTimer(): void {
    if (this.longPollTimer) {
      clearTimeout(this.longPollTimer);
      this.longPollTimer = null;
    }
  }

  send(message: unknown): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      debug.warn('WebSocket not connected, cannot send message');
    }
  }

  onMessage(callback: (message: WebSocketMessage) => void): void {
    this.messageCallback = callback;
  }

  onError(callback: (error: Error) => void): void {
    this.errorCallback = callback;
  }

  onClose(callback: () => void): void {
    this.closeCallback = callback;
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
}

export const websocketService = new WebSocketService();
